import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Label } from 'src/@/components/ui/label';
import { Badge } from 'src/@/components/ui/badge';
import { Separator } from 'src/@/components/ui/separator';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'src/@/components/ui/select';

import { transformStatusTemplateWhatsApp } from 'src/utils/meta.utils';

import v1_actionMessengerCriarTemplate from 'src/actions/v1/meta/messenger/v1_actionMessengerCriarTemplate';
import v1_actionMessengerClonarBiblioteca from 'src/actions/v1/meta/messenger/v1_actionMessengerClonarBiblioteca';
import v1_actionMessengerBiblioteca from 'src/actions/v1/meta/messenger/v1_actionMessengerBiblioteca';
import v1_actionMessengerUploadMidia from 'src/actions/v1/meta/messenger/v1_actionMessengerUploadMidia';

import { TEMPLATES_MESSENGER_DESIGN as D } from './TemplatesMessenger.design';
import MessengerPreview from './MessengerPreview';
import {
    IDIOMAS, extrairPlaceholdersUnicos, montarComponents, validarTemplate,
    slugificar, statusBadgeClasses, statusDotClasses,
    componentePorTipo, textoDoBody, botoesDoTemplate
} from './templatesMessenger.utils';

const FORM_INICIAL = {
    templateName: '',
    language: 'pt_BR',
    parameterFormat: 'POSITIONAL',
    headerType: 'none',
    headerText: '',
    headerHandle: '',
    headerMediaUrl: '',
    headerMediaFilename: '',
    body: '',
    buttons: [],
    variaveis: {},
    description: '',
    tags: ''
};

// Modos: 'criar' | 'biblioteca' | 'ver'
const MODO_UI = {
    criar: { icon: 'eva:plus-circle-outline', iconBox: 'bg-violet-50 text-violet-600', titulo: 'Novo template', subtitulo: 'Utility message da Página' },
    biblioteca: { icon: 'eva:book-open-outline', iconBox: 'bg-blue-50 text-blue-600', titulo: 'Biblioteca da Meta', subtitulo: 'Modelos pré-aprovados — aprovação imediata' },
    ver: { icon: 'eva:eye-outline', iconBox: 'bg-green-50 text-green-600', titulo: 'Visualizar template', subtitulo: 'Somente leitura' }
};

export default function TemplatesMessengerDrawer({
    modo, canal, pagina, clienteId, userId, template,
    onFechar, onSalvo, onExcluir, onEnviarTeste, onTrocarModo
}) {
    const [form, setForm] = useState(FORM_INICIAL);
    const [erro, setErro] = useState(null);
    const [errosValidacao, setErrosValidacao] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [enviandoMidia, setEnviandoMidia] = useState(false);

    // biblioteca
    const [modelos, setModelos] = useState([]);
    const [buscaBiblioteca, setBuscaBiblioteca] = useState('');
    const [carregandoBiblioteca, setCarregandoBiblioteca] = useState(false);
    const [modeloEscolhido, setModeloEscolhido] = useState(null);

    const bodyRef = useRef(null);
    const fileRef = useRef(null);

    const somenteLeitura = modo === 'ver';
    const ui = MODO_UI[modo] || MODO_UI.criar;
    // Identidade da Página vem por prop (de /capacidades): a listagem de canais não
    // projeta `config`, que guarda o access token.
    const nomePagina = pagina?.nome || canal?.nome || '';

    const atualizar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

    // Preenche o formulário quando abre em modo visualização
    useEffect(() => {
        if (modo !== 'ver' || !template) return;

        const header = componentePorTipo(template.components, 'HEADER');
        const formato = String(header?.format || '').toUpperCase();

        setForm({
            ...FORM_INICIAL,
            templateName: template.templateName || template.name,
            language: template.language || 'pt_BR',
            parameterFormat: template.parameter_format || 'POSITIONAL',
            headerType: header ? (formato === 'IMAGE' ? 'IMAGE' : 'TEXT') : 'none',
            headerText: header?.text || '',
            headerHandle: header?.example?.header_handle?.[0] || '',
            headerMediaUrl: header?.mediaUrl || header?.example?.header_url || '',
            body: textoDoBody(template.components),
            buttons: botoesDoTemplate(template.components).map((b) => ({
                type: String(b.type).toUpperCase(),
                text: b.text || '',
                url: b.url || '',
                payload: b.payload || '',
                exemploUrl: b.example?.url_suffix_example || ''
            })),
            description: template.description || '',
            tags: (template.tags || []).join(', ')
        });
    }, [modo, template]);

    // Reseta ao entrar em criação
    useEffect(() => {
        if (modo === 'criar' && !modeloEscolhido) {
            setForm(FORM_INICIAL);
            setErro(null);
            setErrosValidacao([]);
        }
    }, [modo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Busca na biblioteca (debounced)
    useEffect(() => {
        if (modo !== 'biblioteca') return undefined;

        const timer = setTimeout(async () => {
            setCarregandoBiblioteca(true);
            try {
                const res = await v1_actionMessengerBiblioteca(clienteId, {
                    canalId: canal?._id,
                    search: buscaBiblioteca || undefined,
                    language: form.language
                });
                setModelos(res.data?.data?.templates || []);
                setErro(null);
            } catch (e) {
                setModelos([]);
                setErro(e.response?.data?.message || 'Não foi possível carregar a biblioteca da Meta');
            } finally {
                setCarregandoBiblioteca(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [modo, buscaBiblioteca, form.language, canal?._id, clienteId]);

    // Mantém o mapa de variáveis alinhado ao texto: some do texto, some do painel.
    const placeholders = useMemo(() => {
        const doHeader = form.headerType === 'TEXT' ? extrairPlaceholdersUnicos(form.headerText) : [];
        const doBody = extrairPlaceholdersUnicos(form.body);
        const todos = [...new Set([...doHeader, ...doBody])];
        return form.parameterFormat === 'NAMED' ? todos : todos.slice().sort((a, b) => Number(a) - Number(b));
    }, [form.headerText, form.body, form.headerType, form.parameterFormat]);

    const inserirVariavel = () => {
        const proxima = form.parameterFormat === 'NAMED'
            ? 'nome_da_variavel'
            : String(extrairPlaceholdersUnicos(form.body).map(Number).filter(Number.isInteger).reduce((max, n) => Math.max(max, n), 0) + 1);

        const el = bodyRef.current;
        const marcador = `{{${proxima}}}`;

        if (!el) {
            atualizar('body', `${form.body}${marcador}`);
            return;
        }

        const inicio = el.selectionStart ?? form.body.length;
        const fim = el.selectionEnd ?? form.body.length;
        const novo = form.body.slice(0, inicio) + marcador + form.body.slice(fim);
        atualizar('body', novo);

        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(inicio + marcador.length, inicio + marcador.length);
        });
    };

    const aplicarFormatacao = (marcador) => {
        const el = bodyRef.current;
        if (!el) return;
        const inicio = el.selectionStart;
        const fim = el.selectionEnd;
        if (inicio === fim) return;
        const selecionado = form.body.slice(inicio, fim);
        atualizar('body', `${form.body.slice(0, inicio)}${marcador}${selecionado}${marcador}${form.body.slice(fim)}`);
        requestAnimationFrame(() => el.focus());
    };

    const handleUploadImagem = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setEnviandoMidia(true);
        setErro(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('canalId', canal._id);

            const res = await v1_actionMessengerUploadMidia(clienteId, formData);
            const { handle, mediaUrl, originalFilename } = res.data?.data || {};

            setForm((f) => ({
                ...f,
                headerHandle: handle,
                headerMediaUrl: mediaUrl,
                headerMediaFilename: originalFilename
            }));
        } catch (e) {
            setErro(e.response?.data?.message || 'Não foi possível enviar a imagem');
        } finally {
            setEnviandoMidia(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const usarModelo = (modelo) => {
        const corpo = textoDoBody(modelo.components);
        const botoes = botoesDoTemplate(modelo.components).map((b) => ({
            type: String(b.type).toUpperCase(),
            text: b.text || '',
            url: b.url || '',
            payload: b.payload || '',
            exemploUrl: ''
        }));

        // A biblioteca vem com variáveis posicionais e exemplos em bodyParams
        const variaveis = {};
        (modelo.bodyParams || []).forEach((valor, i) => { variaveis[String(i + 1)] = valor; });

        setModeloEscolhido(modelo);
        setForm({
            ...FORM_INICIAL,
            templateName: modelo.name,
            language: modelo.language || form.language,
            parameterFormat: 'POSITIONAL',
            body: corpo,
            buttons: botoes,
            variaveis
        });
        setErro(null);
        setErrosValidacao([]);
        onTrocarModo('criar');
    };

    const handleSalvar = async () => {
        setErro(null);

        const erros = validarTemplate(form);
        if (erros.length) {
            setErrosValidacao(erros);
            setErro(erros[0]);
            return;
        }
        setErrosValidacao([]);

        setSalvando(true);
        try {
            let res;

            if (modeloEscolhido) {
                // Clone da biblioteca: a Meta preenche os componentes a partir do modelo
                res = await v1_actionMessengerClonarBiblioteca(clienteId, {
                    canalId: canal._id,
                    library_template_name: modeloEscolhido.libraryTemplateName || modeloEscolhido.name,
                    templateName: form.templateName,
                    language: form.language,
                    library_template_body_inputs: [{ type: 'body', text: form.body }],
                    ...(form.buttons.length ? {
                        library_template_button_inputs: form.buttons.map((b) => (
                            b.type === 'URL'
                                ? { type: 'URL', text: b.text, url: { base_url: b.url } }
                                : { type: 'POSTBACK', text: b.text, payload: b.payload }
                        ))
                    } : {}),
                    description: form.description,
                    tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
                    userId
                });
            } else {
                res = await v1_actionMessengerCriarTemplate(clienteId, {
                    canalId: canal._id,
                    templateName: form.templateName,
                    language: form.language,
                    parameter_format: form.parameterFormat,
                    components: montarComponents(form),
                    description: form.description,
                    tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
                    userId
                });
            }

            setModeloEscolhido(null);
            onSalvo(res.data?.data?.template);
        } catch (e) {
            const dados = e.response?.data;
            setErro(dados?.message || 'Não foi possível salvar o template');
            if (dados?.details?.erros) setErrosValidacao(dados.details.erros);
        } finally {
            setSalvando(false);
        }
    };

    // ---- render

    const secao = (icone, titulo, children) => (
        <div className={`${D.drawer.content.section.container} ${D.drawer.content.section.card}`}>
            <div className={D.drawer.content.section.titleWrapper}>
                <div className={D.drawer.content.section.iconBox}>
                    <Iconify icon={icone} width={16} />
                </div>
                <h3 className={D.drawer.content.section.title}>{titulo}</h3>
            </div>
            {children}
        </div>
    );

    return (
        <div className={D.drawer.overlay}>
            <div className={D.drawer.backdrop} onClick={onFechar} />

            <div className={D.drawer.container}>
                {/* Header */}
                <div className={D.drawer.header.container}>
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ui.iconBox}`}>
                            <Iconify icon={ui.icon} width={20} />
                        </div>
                        <div>
                            <h2 className={D.drawer.header.title}>
                                {modo === 'ver' ? (template?.templateName || template?.name) : ui.titulo}
                            </h2>
                            <p className={D.drawer.header.subtitle}>
                                {modo === 'ver' ? ui.subtitulo : `${ui.subtitulo} · ${nomePagina}`}
                            </p>
                        </div>
                        {modo === 'ver' && template && (
                            <Badge
                                variant="outline"
                                className={`ml-2 text-xs font-medium flex items-center gap-1.5 ${statusBadgeClasses(template.status)}`}
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${statusDotClasses(template.status)}`} />
                                {transformStatusTemplateWhatsApp(template.status)}
                            </Badge>
                        )}
                    </div>

                    <button onClick={onFechar} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <Iconify icon="eva:close-outline" width={20} />
                    </button>
                </div>

                {/* Erro inline (banner, não overlay) */}
                {erro && (
                    <div className={D.drawer.errorBanner}>
                        <Iconify icon="eva:alert-circle-outline" width={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-sm text-red-800">{erro}</p>
                            {errosValidacao.length > 1 && (
                                <ul className="mt-1 space-y-0.5">
                                    {errosValidacao.slice(1).map((e, i) => (
                                        <li key={i} className="text-xs text-red-700">• {e}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {template?.status === 'REJECTED' && template?.rejectionReason && (
                    <div className="flex items-start gap-3 bg-amber-50 border-b border-amber-100 px-6 py-3">
                        <Iconify icon="eva:alert-triangle-outline" width={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            <span className="font-semibold">Motivo da recusa: </span>{template.rejectionReason}
                        </p>
                    </div>
                )}

                {/* Conteúdo */}
                {modo === 'biblioteca' ? (
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                        <div className="relative mb-5 max-w-md">
                            <Iconify icon="eva:search-outline" width={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                placeholder="Buscar por nome ou conteúdo (ex: order)..."
                                value={buscaBiblioteca}
                                onChange={(e) => setBuscaBiblioteca(e.target.value)}
                                className={D.filters.searchInput}
                            />
                        </div>

                        {carregandoBiblioteca && (
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden mb-5">
                                <div className="h-full bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
                            </div>
                        )}

                        {!carregandoBiblioteca && modelos.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                                    <Iconify icon="eva:book-outline" width={32} className="text-gray-300" />
                                </div>
                                <p className="text-sm font-medium text-gray-900 mb-1">Nenhum modelo encontrado</p>
                                <p className="text-xs text-gray-500">Tente outro termo ou idioma.</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {modelos.map((modelo) => (
                                <div key={`${modelo.name}-${modelo.language}`} className={`${D.card.container} group`} onClick={() => usarModelo(modelo)}>
                                    <div className="bg-gray-50 p-3 border-b border-gray-100">
                                        <MessengerPreview
                                            compacto
                                            body={textoDoBody(modelo.components)}
                                            buttons={botoesDoTemplate(modelo.components)}
                                        />
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <h4 className="text-sm font-semibold text-gray-900 truncate">{modelo.name}</h4>
                                            <Badge variant="outline" className="text-[10px] text-gray-600 border-gray-200 bg-gray-50 flex-shrink-0">
                                                {modelo.language}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-gray-500 line-clamp-2">
                                            {[modelo.topic, modelo.usecase].filter(Boolean).join(' · ') || 'Modelo utilitário da Meta'}
                                        </p>
                                        <p className="mt-3 text-xs font-medium text-[#7C3AED] group-hover:underline">Usar modelo</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className={D.drawer.content.container}>
                        {/* Coluna esquerda: formulário */}
                        <div className={D.drawer.content.leftColumn}>
                            {modeloEscolhido && (
                                <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5">
                                    <Iconify icon="eva:book-open-outline" width={18} className="text-blue-600 flex-shrink-0" />
                                    <p className="text-sm text-blue-800 flex-1">
                                        Baseado no modelo <span className="font-semibold">{modeloEscolhido.name}</span> — a aprovação é imediata.
                                    </p>
                                    <button
                                        onClick={() => { setModeloEscolhido(null); setForm(FORM_INICIAL); }}
                                        className="text-xs text-blue-700 hover:underline flex-shrink-0"
                                    >
                                        Descartar
                                    </button>
                                </div>
                            )}

                            {secao('eva:info-outline', 'Identificação', (
                                <div className="space-y-4">
                                    <div>
                                        <Label className={D.drawer.content.section.label}>Nome do template *</Label>
                                        <Input
                                            value={form.templateName}
                                            onChange={(e) => atualizar('templateName', e.target.value)}
                                            placeholder="Ex: Confirmação de pedido"
                                            disabled={somenteLeitura}
                                            className={`${D.drawer.content.section.input.base} ${somenteLeitura ? D.drawer.content.section.input.disabled : ''}`}
                                        />
                                        {form.templateName && (
                                            <p className="text-[11px] text-gray-400 mt-1.5 font-mono">
                                                Enviado à Meta como: {slugificar(form.templateName) || '—'}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className={D.drawer.content.section.label}>Idioma *</Label>
                                            <Select value={form.language} onValueChange={(v) => atualizar('language', v)} disabled={somenteLeitura}>
                                                <SelectTrigger className={D.drawer.content.section.input.base}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                                    {IDIOMAS.map((i) => (
                                                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <Label className={D.drawer.content.section.label}>
                                                Categoria
                                                <Iconify
                                                    icon="eva:info-outline"
                                                    width={13}
                                                    className="text-gray-400"
                                                    title="Páginas do Facebook só criam templates UTILITY — marketing não é permitido nesse canal."
                                                />
                                            </Label>
                                            <div className="flex items-center h-10 px-3 bg-gray-50 border border-gray-200 rounded-xl">
                                                <Badge variant="outline" className="text-xs text-gray-600 border-gray-200 bg-white">
                                                    Utilitário / Serviço
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    {!somenteLeitura && !modeloEscolhido && (
                                        <div>
                                            <Label className={D.drawer.content.section.label}>Formato das variáveis</Label>
                                            <div className="flex gap-2">
                                                {[
                                                    { valor: 'POSITIONAL', label: 'Posicional', exemplo: '{{1}}' },
                                                    { valor: 'NAMED', label: 'Nomeado', exemplo: '{{nome}}' }
                                                ].map((op) => (
                                                    <button
                                                        key={op.valor}
                                                        type="button"
                                                        onClick={() => setForm((f) => ({ ...f, parameterFormat: op.valor, variaveis: {} }))}
                                                        className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                                                            form.parameterFormat === op.valor
                                                                ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED]'
                                                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {op.label}
                                                        <span className="ml-1.5 font-mono text-[11px] opacity-70">{op.exemplo}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-[11px] text-gray-400 mt-1.5">
                                                Trocar o formato limpa os valores de exemplo.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <Separator className="bg-gray-200/60 my-6" />

                            {secao('eva:text-outline', 'Título (opcional)', (
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        {[
                                            { valor: 'none', label: 'Sem título', icone: 'eva:minus-outline' },
                                            { valor: 'TEXT', label: 'Texto', icone: 'eva:text-outline' },
                                            { valor: 'IMAGE', label: 'Imagem', icone: 'eva:image-outline' }
                                        ].map((op) => (
                                            <button
                                                key={op.valor}
                                                type="button"
                                                disabled={somenteLeitura}
                                                onClick={() => atualizar('headerType', op.valor)}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-all disabled:opacity-60 ${
                                                    form.headerType === op.valor
                                                        ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED]'
                                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Iconify icon={op.icone} width={15} />
                                                {op.label}
                                            </button>
                                        ))}
                                    </div>

                                    {form.headerType === 'TEXT' && (
                                        <Input
                                            value={form.headerText}
                                            onChange={(e) => atualizar('headerText', e.target.value)}
                                            placeholder="Ex: Atualização do {{1}}"
                                            disabled={somenteLeitura}
                                            className={D.drawer.content.section.input.base}
                                        />
                                    )}

                                    {form.headerType === 'IMAGE' && (
                                        <div>
                                            {form.headerMediaUrl ? (
                                                <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                                                    <img src={form.headerMediaUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-gray-900 truncate">{form.headerMediaFilename || 'Imagem'}</p>
                                                        <p className="text-[11px] text-gray-400">Enviada à Meta</p>
                                                    </div>
                                                    {!somenteLeitura && (
                                                        <button
                                                            onClick={() => setForm((f) => ({ ...f, headerHandle: '', headerMediaUrl: '', headerMediaFilename: '' }))}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        >
                                                            <Iconify icon="eva:trash-2-outline" width={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <input
                                                        ref={fileRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleUploadImagem}
                                                        className="hidden"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        disabled={somenteLeitura || enviandoMidia}
                                                        onClick={() => fileRef.current?.click()}
                                                        className="w-full border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl h-auto py-4"
                                                    >
                                                        <Iconify
                                                            icon={enviandoMidia ? 'line-md:loading-twotone-loop' : 'eva:cloud-upload-outline'}
                                                            width={18}
                                                            className="mr-2"
                                                        />
                                                        {enviandoMidia ? 'Enviando...' : 'Enviar imagem'}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <Separator className="bg-gray-200/60 my-6" />

                            {secao('eva:message-square-outline', 'Mensagem', (
                                <div className="space-y-4">
                                    {!somenteLeitura && (
                                        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 w-fit">
                                            {[
                                                { marcador: '*', icone: 'eva:text-outline', titulo: 'Negrito' },
                                                { marcador: '_', icone: 'eva:italic', titulo: 'Itálico' },
                                                { marcador: '~', icone: 'eva:minus-outline', titulo: 'Riscado' }
                                            ].map((op) => (
                                                <button
                                                    key={op.marcador}
                                                    type="button"
                                                    title={op.titulo}
                                                    onClick={() => aplicarFormatacao(op.marcador)}
                                                    className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-md text-gray-600 transition-colors"
                                                >
                                                    <Iconify icon={op.icone} width={14} />
                                                </button>
                                            ))}
                                            <div className="w-px h-5 bg-gray-200 mx-1" />
                                            <button
                                                type="button"
                                                onClick={inserirVariavel}
                                                className="px-2 h-7 flex items-center gap-1 hover:bg-violet-50 rounded-md text-[#7C3AED] text-xs font-medium transition-colors"
                                            >
                                                <Iconify icon="eva:plus-outline" width={13} />
                                                Variável
                                            </button>
                                        </div>
                                    )}

                                    <Textarea
                                        ref={bodyRef}
                                        value={form.body}
                                        onChange={(e) => atualizar('body', e.target.value)}
                                        placeholder="Ex: Boas notícias! Seu pedido #{{1}} já está a caminho e chega em breve."
                                        rows={6}
                                        disabled={somenteLeitura}
                                        className={`${D.drawer.content.section.input.base} resize-none ${somenteLeitura ? D.drawer.content.section.input.disabled : ''}`}
                                    />
                                    <p className="text-[11px] text-gray-400">
                                        A Meta recusa mensagens que começam ou terminam com uma variável.
                                    </p>

                                    {placeholders.length > 0 && (
                                        <div className={D.drawer.variaveis.container}>
                                            <p className="text-xs font-bold text-violet-900 uppercase tracking-wider mb-3">
                                                Valores de exemplo
                                            </p>
                                            <div className="space-y-2.5">
                                                {placeholders.map((ph) => (
                                                    <div key={ph} className="flex items-center gap-3">
                                                        <span className={`${D.drawer.variaveis.chip} flex-shrink-0`}>{`{{${ph}}}`}</span>
                                                        <Input
                                                            value={form.variaveis[ph] || ''}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                variaveis: { ...f.variaveis, [ph]: e.target.value }
                                                            }))}
                                                            placeholder="Valor de exemplo"
                                                            disabled={somenteLeitura}
                                                            className={`${D.drawer.content.section.input.base} h-9 bg-white`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-[11px] text-violet-700/70 mt-3">
                                                Obrigatório: é o que a Meta usa para revisar o template.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <Separator className="bg-gray-200/60 my-6" />

                            {secao('eva:external-link-outline', 'Botões (opcional)', (
                                <div className="space-y-3">
                                    {form.buttons.map((btn, i) => (
                                        <div key={i} className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={btn.type}
                                                    onValueChange={(v) => setForm((f) => ({
                                                        ...f,
                                                        buttons: f.buttons.map((b, j) => (j === i ? { ...b, type: v } : b))
                                                    }))}
                                                    disabled={somenteLeitura}
                                                >
                                                    <SelectTrigger className={`${D.drawer.content.section.input.base} w-32 h-9 bg-white`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                                        <SelectItem value="URL">Link</SelectItem>
                                                        <SelectItem value="POSTBACK">Resposta</SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                <Input
                                                    value={btn.text}
                                                    onChange={(e) => setForm((f) => ({
                                                        ...f,
                                                        buttons: f.buttons.map((b, j) => (j === i ? { ...b, text: e.target.value } : b))
                                                    }))}
                                                    placeholder="Texto do botão"
                                                    disabled={somenteLeitura}
                                                    className={`${D.drawer.content.section.input.base} h-9 bg-white flex-1`}
                                                />

                                                {!somenteLeitura && (
                                                    <button
                                                        onClick={() => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }))}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                                    >
                                                        <Iconify icon="eva:trash-2-outline" width={16} />
                                                    </button>
                                                )}
                                            </div>

                                            {btn.type === 'URL' ? (
                                                <>
                                                    <Input
                                                        value={btn.url}
                                                        onChange={(e) => setForm((f) => ({
                                                            ...f,
                                                            buttons: f.buttons.map((b, j) => (j === i ? { ...b, url: e.target.value } : b))
                                                        }))}
                                                        placeholder="https://site.com/pedidos/{{1}}"
                                                        disabled={somenteLeitura}
                                                        className={`${D.drawer.content.section.input.base} h-9 bg-white`}
                                                    />
                                                    {extrairPlaceholdersUnicos(btn.url).length > 0 && (
                                                        <Input
                                                            value={btn.exemploUrl}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                buttons: f.buttons.map((b, j) => (j === i ? { ...b, exemploUrl: e.target.value } : b))
                                                            }))}
                                                            placeholder="Exemplo da URL completa: https://site.com/pedidos/1234"
                                                            disabled={somenteLeitura}
                                                            className={`${D.drawer.content.section.input.base} h-9 bg-white`}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <Input
                                                    value={btn.payload}
                                                    onChange={(e) => setForm((f) => ({
                                                        ...f,
                                                        buttons: f.buttons.map((b, j) => (j === i ? { ...b, payload: e.target.value } : b))
                                                    }))}
                                                    placeholder="payload_do_botao ou pedido_{{1}}"
                                                    disabled={somenteLeitura}
                                                    className={`${D.drawer.content.section.input.base} h-9 bg-white`}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {!somenteLeitura && form.buttons.length < 3 && (
                                        <Button
                                            variant="outline"
                                            onClick={() => setForm((f) => ({
                                                ...f,
                                                buttons: [...f.buttons, { type: 'URL', text: '', url: '', payload: '', exemploUrl: '' }]
                                            }))}
                                            className="w-full border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl"
                                        >
                                            <Iconify icon="eva:plus-outline" width={16} className="mr-1.5" />
                                            Adicionar botão
                                        </Button>
                                    )}
                                </div>
                            ))}

                            {!somenteLeitura && (
                                <>
                                    <Separator className="bg-gray-200/60 my-6" />
                                    {secao('eva:pricetags-outline', 'Organização', (
                                        <div className="space-y-4">
                                            <div>
                                                <Label className={D.drawer.content.section.label}>Descrição interna</Label>
                                                <Input
                                                    value={form.description}
                                                    onChange={(e) => atualizar('description', e.target.value)}
                                                    placeholder="Para que serve este template"
                                                    className={D.drawer.content.section.input.base}
                                                />
                                            </div>
                                            <div>
                                                <Label className={D.drawer.content.section.label}>Tags</Label>
                                                <Input
                                                    value={form.tags}
                                                    onChange={(e) => atualizar('tags', e.target.value)}
                                                    placeholder="pedidos, entrega (separadas por vírgula)"
                                                    className={D.drawer.content.section.input.base}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Coluna direita: preview */}
                        <div className={D.drawer.content.rightColumn}>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Pré-visualização</p>

                            <MessengerPreview
                                pageName={nomePagina}
                                pageProfile={pagina?.profile}
                                headerType={form.headerType}
                                headerText={form.headerText}
                                headerImageUrl={form.headerMediaUrl}
                                body={form.body}
                                buttons={form.buttons}
                                valores={form.variaveis}
                            />

                            <div className="mt-5 bg-white rounded-xl border border-gray-100 p-4 space-y-2.5">
                                {[
                                    ['Página', nomePagina || '—'],
                                    ['Idioma', form.language],
                                    ['Categoria', 'UTILITY'],
                                    ['Variáveis', placeholders.length ? `${placeholders.length} (${form.parameterFormat === 'NAMED' ? 'nomeadas' : 'posicionais'})` : 'nenhuma'],
                                    ['Botões', form.buttons.length || 'nenhum']
                                ].map(([rotulo, valor]) => (
                                    <div key={rotulo} className="flex items-center justify-between gap-3">
                                        <span className="text-xs text-gray-500">{rotulo}</span>
                                        <span className="text-xs font-medium text-gray-900 truncate">{valor}</span>
                                    </div>
                                ))}
                            </div>

                            {(form.headerType !== 'none' || form.buttons.length > 0) && (
                                <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
                                    Com título ou botões, a Meta entrega a mensagem como card estruturado —
                                    e não como um balão de texto simples.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Footer: ações à direita, secundárias à esquerda */}
                <div className={D.drawer.footer.container}>
                    <div className="flex items-center gap-2">
                        {modo === 'ver' && (
                            <Button
                                variant="outline"
                                onClick={() => onExcluir(template)}
                                className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                            >
                                <Iconify icon="eva:trash-2-outline" width={16} className="mr-1.5" />
                                Excluir
                            </Button>
                        )}
                        {modo === 'criar' && (
                            <Button
                                variant="outline"
                                onClick={() => onTrocarModo('biblioteca')}
                                className={D.page.secondaryButton}
                            >
                                <Iconify icon="eva:book-outline" width={16} className="mr-1.5" />
                                Ver biblioteca
                            </Button>
                        )}
                        {modo === 'biblioteca' && (
                            <Button variant="outline" onClick={() => onTrocarModo('criar')} className={D.page.secondaryButton}>
                                <Iconify icon="eva:arrow-back-outline" width={16} className="mr-1.5" />
                                Criar do zero
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={onFechar} className="text-gray-600 rounded-xl">
                            {modo === 'ver' ? 'Fechar' : 'Cancelar'}
                        </Button>

                        {modo === 'ver' ? (
                            <button
                                onClick={() => onEnviarTeste(template)}
                                disabled={template?.status !== 'APPROVED'}
                                className={`${D.page.actionButton} disabled:opacity-50 disabled:cursor-not-allowed`}
                                title={template?.status !== 'APPROVED' ? 'Só é possível enviar templates aprovados' : undefined}
                            >
                                <Iconify icon="eva:paper-plane-outline" width={16} />
                                Enviar teste
                            </button>
                        ) : modo === 'criar' ? (
                            <button onClick={handleSalvar} disabled={salvando} className={`${D.page.actionButton} disabled:opacity-60`}>
                                <Iconify icon={salvando ? 'line-md:loading-twotone-loop' : 'eva:checkmark-outline'} width={16} />
                                {salvando ? 'Enviando à Meta...' : 'Criar template'}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

TemplatesMessengerDrawer.propTypes = {
    modo: PropTypes.oneOf(['criar', 'biblioteca', 'ver']).isRequired,
    canal: PropTypes.object,
    pagina: PropTypes.shape({ nome: PropTypes.string, profile: PropTypes.string }),
    clienteId: PropTypes.string,
    userId: PropTypes.string,
    template: PropTypes.object,
    onFechar: PropTypes.func.isRequired,
    onSalvo: PropTypes.func.isRequired,
    onExcluir: PropTypes.func.isRequired,
    onEnviarTeste: PropTypes.func.isRequired,
    onTrocarModo: PropTypes.func.isRequired
};
